import { useCallback, useEffect, useRef } from "react";
import type { Task } from "../../agent";
import { useAgentSdk } from "../../agent/hooks";
import { getSessionByTaskId } from "../../sessions";
import { useSessionStore } from "../session.store";

const getTimeRemaining = (expirationTimestamp: number) => Math.max(expirationTimestamp - Date.now(), 0);

/**
 * A hook to manage wrapping state timers for concurrent sessions
 * Schedules session wrapup expiration based on session wrapping state
 * Handles race condition between task status changes and session wrapping state
 */
export function useWrapupTimer() {
    const timeoutMap = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const agentSdk = useAgentSdk();

    // Track tasks in wrapping state to handle session updates
    const wrappingTasksRef = useRef<Map<string, Task>>(new Map());
    // Track session wrapping state to optimize comparison
    const sessionWrappingStateRef = useRef(new Map<string, number | undefined>());

    /**
     * For a given task, returns the time remaining for its wrapping state.
     * Returns 0 if no session or no expiration timestamp or if the timer has already expired
     */
    const getWrappingTimeRemaining = useCallback((task: Task) => {
        const session = getSessionByTaskId(task.id);
        if (!session || !session.wrappingState?.expirationTimestamp) {
            return 0;
        } else return getTimeRemaining(session.wrappingState.expirationTimestamp);
    }, []);

    const cleanupTimeout = useCallback((sessionId: string): void => {
        const timeout = timeoutMap.current.get(sessionId);
        if (timeout) {
            clearTimeout(timeout);
            timeoutMap.current.delete(sessionId);
        }
    }, []);

    const endTaskWrapup = useCallback(
        async (task: Task): Promise<void> => {
            await agentSdk.endWrapup("WrappingTimeExpired", task.partner, task);
        },
        [agentSdk],
    );

    const scheduleEndWrapup = useCallback(
        (task: Task): void => {
            const session = getSessionByTaskId(task.id);

            if (!session) return;

            if (!timeoutMap.current.has(session.id)) {
                // Ensure we have a valid wrapping state with expiration timestamp
                if (!session.wrappingState?.expirationTimestamp) {
                    return; // Don't schedule without a proper expiration timestamp
                }

                const timeRemaining = getTimeRemaining(session.wrappingState.expirationTimestamp);

                timeoutMap.current.set(
                    session.id,
                    setTimeout(() => {
                        void endTaskWrapup(task).finally(() => timeoutMap.current.delete(session.id));
                    }, timeRemaining),
                );
            }
        },
        [endTaskWrapup],
    );

    const cleanupInactiveSessions = useCallback(
        (activeSessionIds: Set<string>): void => {
            Array.from(timeoutMap.current.keys()).forEach((sessionId) => {
                if (!activeSessionIds.has(sessionId)) {
                    cleanupTimeout(sessionId);
                }
            });
        },
        [cleanupTimeout],
    );

    /**
     * Schedules wrapup for tasks that are currently in the wrapping state.
     * Cleans up any existing timers for sessions that are no longer active.
     */
    const scheduleWrappingForTasks = useCallback(
        (tasks: Task[]) => {
            const activeSessionIds = new Set<string>();
            const wrappingTasks = tasks.filter((task) => task.status === "wrapping");

            // Clear and update the wrapping tasks map
            wrappingTasksRef.current.clear();
            wrappingTasks.forEach((task) => wrappingTasksRef.current.set(task.id, task));

            sessionWrappingStateRef.current.clear();

            wrappingTasks.forEach((task) => {
                const session = getSessionByTaskId(task.id);
                if (!session) return;

                sessionWrappingStateRef.current.set(session.id, session.wrappingState?.expirationTimestamp);
                activeSessionIds.add(session.id);

                // Ensuring no race condition - only schedule if wrapping state exists
                if (session.wrappingState?.expirationTimestamp) {
                    scheduleEndWrapup(task);
                }
            });

            // Clean up timers for sessions that are no longer active
            cleanupInactiveSessions(activeSessionIds);
        },
        [scheduleEndWrapup, cleanupInactiveSessions],
    );

    const scheduleSpecificTasks = useCallback(
        (tasksToSchedule: Task[]) => {
            if (tasksToSchedule.length === 0) return;

            const activeSessionIds = new Set<string>();

            tasksToSchedule.forEach((task) => {
                const session = getSessionByTaskId(task.id);
                if (!session) return;

                sessionWrappingStateRef.current.set(session.id, session.wrappingState?.expirationTimestamp);
                activeSessionIds.add(session.id);

                // Clear any existing timeout for this session before scheduling a new one
                if (timeoutMap.current.has(session.id)) {
                    cleanupTimeout(session.id);
                }

                // Schedule if wrapping state exists
                if (session.wrappingState?.expirationTimestamp) {
                    scheduleEndWrapup(task);
                }
            });
        },
        [scheduleEndWrapup, cleanupTimeout],
    );

    // ! important ! Listening for session store updates to handle race conditions
    useEffect(() => {
        // Track session wrapping state changes for each wrapping task
        const unsubscribe = useSessionStore.subscribe((state, previousState) => {
            if (wrappingTasksRef.current.size === 0) return;

            const changedTasks: Task[] = [];

            // Check each wrapping task for relevant changes
            wrappingTasksRef.current.forEach((task) => {
                const currentSession = state.getSessionByTaskId?.(task.id);
                const previousSession = previousState.getSessionByTaskId?.(task.id);

                const currentExpTimestamp = currentSession?.wrappingState?.expirationTimestamp;
                const prevExpTimestamp = previousSession?.wrappingState?.expirationTimestamp;

                // If the expiration timestamp has changed, we need to reschedule
                if (currentExpTimestamp && currentExpTimestamp !== prevExpTimestamp) {
                    changedTasks.push(task);
                }
            });

            if (changedTasks.length > 0) {
                scheduleSpecificTasks(changedTasks);
            }
        });

        return unsubscribe;
    }, [scheduleSpecificTasks]);

    // Clean up all timers when component unmounts
    useEffect(() => {
        const timeoutsToCleanup = timeoutMap.current;
        return () => {
            timeoutsToCleanup.forEach((timeout) => clearTimeout(timeout));
            timeoutsToCleanup.clear();
        };
    }, []);

    return {
        getWrappingTimeRemaining,
        scheduleEndWrapup,
        scheduleWrappingForTasks,
    };
}
